import React from "react";
import { useTranslation } from "react-i18next";
import Input from "../elements/form/input";
import { formatPhoneNumber } from "../PhoneInput/CustomPhoneInput";

interface Props {
  idPrefix: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  onPhoneBlur?: () => void;
  phoneError?: string;
  /** Mobile number is always shown, but only editable once SMS is ticked. */
  disabled?: boolean;
  /** Full-width layout for narrow panels (no indent under the checkbox). */
  compact?: boolean;
}

/** Optional SMS consent: checkbox + fixed disclosure + mobile number. */
const SmsConsentFields: React.FC<Props> = ({
  idPrefix,
  checked,
  onCheckedChange,
  phone,
  onPhoneChange,
  onPhoneBlur,
  phoneError,
  disabled,
  compact,
}) => {
  const { t } = useTranslation("consent");
  return (
    <div className="space-y-3">
      <label htmlFor={`${idPrefix}-sms`} className="flex items-start gap-2.5 cursor-pointer">
        <input
          id={`${idPrefix}-sms`}
          type="checkbox"
          className="checkbox checkbox-sm shrink-0"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange(e.target.checked)}
        />
        <span className="text-md text-gray-800">{t("sms.checkbox")}</span>
      </label>
      <p className={`p-3 text-sm leading-relaxed text-gray-500 rounded-lg bg-gray-50 ${compact ? "" : "ml-7"}`}>
        {t("sms.disclosure")}
      </p>
      <div className={compact ? "" : "pl-7"}>
        {/* Own label: the shared Input label inherits the drawer's light text colour and vanishes there. */}
        <label htmlFor={`${idPrefix}-phone`} className="block text-base leading-tight text-gray-800">
          {t("sms.mobileNumber")}
          <span className="ml-1 text-red-600">*</span>
        </label>
        <Input
          type="tel"
          name={`${idPrefix}-phone`}
          id={`${idPrefix}-phone`}
          placeholder={t("sms.mobilePlaceholder")}
          className="!my-0"
          disabled={!checked || disabled}
          value={phone}
          error={phoneError}
          onBlur={onPhoneBlur}
          onChange={(e) => onPhoneChange(formatTypedPhone(e.target.value))}
        />
      </div>
    </div>
  );
};

/** Same (XXX) XXX-XXXX masking CustomPhoneInput applies, for plain controlled state. */
export const formatTypedPhone = (raw: string): string => {
  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length === 10) return formatPhoneNumber(digits);
  if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return digits ? `(${digits}` : "";
};

export default SmsConsentFields;
